import { FbmResource } from 'models/fbm-resource/_types';
import { Service } from 'models/service/_types';
import { User } from 'models/user/_types';
import { ScheduledMembership, Visit } from 'models/visit/_types';
import { ScheduledAppointment } from 'src/models/scheduled-appointment/scheduled-appointment';
import { Identifier } from 'types/utilityTypes';

export enum AppointmentStatus {
  Scheduled = 'scheduled',
  Completed = 'completed',
  Canceled = 'cancelled',
  LateCanceled = 'late_cancelled',
}

export interface ZoomSettings {
  password?: string;
  description?: string;
  autoRecording?: string;
  muteUponEntry?: boolean;
  waitingRoom?: boolean;
  joinBeforeHost?: boolean;
}

export interface Appointment {
  id: Identifier;
  scheduleId: Identifier;
  scheduledAppointmentId: Identifier;
  status: AppointmentStatus;
  rescheduleDate: string;
  serviceName: string;
  cancellationFee: number;
  noshowFee: number;
  locationName: string;
  trainerIds: Identifier[];
  primaryTrainerNames: string[];
  planWorkoutIds: Identifier[];
  trainerNames: string[];
  startTime: Date;
  endTime: Date;
  expiresOn: Date | null;
  notes: string;
  description: string;
  calendarId: Identifier;
  remainingVisits: number;
  isGroupClass: boolean;
  maxVisits: number;
  zoomMeeting: ZoomMeeting;

  // Edit-specific fields
  disableEmail?: boolean;
  noTrainers?: boolean;
  noResources?: boolean;
  overrideMaxVisits?: boolean;
  serviceResourceRules?: ServiceResourceRule[];
  zoom?: ZoomSettings | null;
  cancelledAt?: Date | null;

  // associations
  visits: Visit[];
  entries: WaitListEntry[];
  service: Partial<Service>;
  location: Location;
  primaryTrainers: User[];
  secondaryTrainers: User[];
  secondaryTrainerNames: string[];
  resources: FbmResource[];
  resourceNames: string[];

  // Recurring session data (populated when scheduledAppointmentId exists)
  scheduledAppointment?: ScheduledAppointment | null;
  scheduledMembers?: ScheduledMembership[] | null;

  // id versions of associations
  serviceId: Identifier;
  locationId: Identifier;
  primaryTrainerIds: Identifier[];
  secondaryTrainerIds: Identifier[];
}

export interface ServiceResourceRule {
  resourceId: Identifier | null;
  usageBehavior: string;
  subResourceIds?: Identifier[];
}

export interface Location {
  id: Identifier;
  name: string;
  address: string;
}

export interface WaitListEntry {
  id: Identifier;
  user?: User;
  userId: Identifier;
  clientId?: Identifier;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  status: string;
  appointmentId: Identifier;
  createdAt: Date;
}

interface ZoomMeeting {
  id: Identifier;
  joinUrl: string;
  startUrl: string;
  password: string;
}

export interface EligibilityCheck {
  eligible: boolean;
  reason: string;
  data: any;
  count: number;
  interval: number;
  trainerReason: string;
  reasonMessage: string;
  visitsRemaining: number;
}

// Conflict checking types
export interface ConflictCheckParams {
  primaryTrainerIds: Identifier[];
  secondaryTrainerIds?: Identifier[];
  startTime: Date;
  endTime: Date;
  appointmentId?: Identifier;
  locationId?: Identifier | null;
  noResources?: boolean;
  serviceResourceRules?: ServiceResourceRule[];
  // Recurring params (optional)
  scheduledAppointmentId?: Identifier;
  recurringType?: string;
  recurringCount?: number;
  startAt?: string;
  endAt?: string | null;
  selectedDays?: number[];
}

export interface ConflictCheckResponse {
  hasConflicts: boolean;
  totalConflicts: number;
  conflicts: Conflict[];
}

export interface Conflict {
  startTime: number; // Unix timestamp
  endTime: number; // Unix timestamp
  conflictType: 'staff' | 'resource';
  trainerNames?: string;
  serviceName?: string;
  resourceName?: string;
  locationName?: string;
}
